/**
 * TPU Premium Hat Patches — guided "Change quantity" flow.
 *
 * Cart/mini-cart quantity for TPU lines is deliberately locked (see
 * sections/main-cart.liquid, snippets/mini-cart.liquid) because DPO only
 * computes correct tiered pricing (volume rate, per-patch floor, setup-fee
 * waiver) on the product page at add-to-cart time — Shopify's native cart
 * quantity +/- just multiplies whatever per-unit price got frozen at that
 * moment, silently over/undercharging. Confirmed against Itoris's own user
 * guide: there is no documented way to recalculate or set price for a line
 * already in the cart.
 *
 * This is the safe alternative: a "Change quantity" link (added next to
 * Remove) that routes back here with ?tpu_requote={old line's cart key}.
 * On load we pre-fill the old line's width/height/film type/quantity into
 * the live product form and let DPO compute the real price through its own
 * normal, working mechanism — the customer reviews it and clicks Add to
 * Cart themselves (not auto-submitted; see the consent-checkbox reasoning
 * below). Only once that succeeds do we remove the old line, and only via
 * this file's own /cart/update.js call — never before, so an abandoned
 * requote leaves the original order untouched.
 *
 * Uploaded file: attempts a silent carry-over of the same file (by its
 * Uploadcare UUID, already known extractable from the "Uploaded Image"
 * property — same regex tpu-file-preview.js uses) rather than asking the
 * customer to re-upload. This is unverified against Upload Kit's own
 * (closed-source, remote) submission logic — if the synthetic thumbnail
 * fails to load, this falls back to clearing it and asking the customer to
 * re-upload rather than risk an order with a broken file reference.
 */
(function () {
  var LOG = '[tpu-change-quantity]';
  var params = new URLSearchParams(location.search);
  var oldKey = params.get('tpu_requote');
  if (!oldKey) return;

  function findProperty(props, keyword) {
    for (var key in props) {
      if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
      if (key.charAt(0) === '_') continue; // hidden system properties (e.g. this file's own marker)
      if (key.toLowerCase().indexOf(keyword.toLowerCase()) !== -1) return props[key];
    }
    return null;
  }

  function showBanner(message, kind) {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var el = document.querySelector('.tpu-requote-banner');
    if (!el) {
      el = document.createElement('div');
      el.className = 'tpu-requote-banner alert alert--tight';
      form.parentNode.insertBefore(el, form);
    }
    el.className = 'tpu-requote-banner alert alert--tight' + (kind ? ' alert--' + kind : '');
    el.textContent = message;
  }

  function waitFor(check, callback, timeoutMs) {
    var done = false;
    function tryNow() {
      if (done) return;
      var result = check();
      if (result) {
        done = true;
        observer.disconnect();
        clearTimeout(timer);
        callback(result);
      }
    }
    var observer = new MutationObserver(tryNow);
    observer.observe(document.body, { childList: true, subtree: true });
    var timer = setTimeout(function () {
      if (done) return;
      done = true;
      observer.disconnect();
      console.log(LOG, 'timed out waiting for product form fields');
    }, timeoutMs || 8000);
    tryNow();
  }

  function selectFilmType(root, label) {
    if (!label) return false;
    var candidates = root.querySelectorAll('.choice, [class*="choice"], [class*="swatch"], button');
    var target = null;
    var wanted = label.trim().toLowerCase();
    for (var i = 0; i < candidates.length; i++) {
      var text = (candidates[i].textContent || '').trim().toLowerCase();
      if (text === wanted) { target = candidates[i]; break; }
    }
    if (!target) {
      for (var j = 0; j < candidates.length; j++) {
        var t2 = (candidates[j].textContent || '').trim().toLowerCase();
        if (t2 && wanted.indexOf(t2) !== -1) { target = candidates[j]; break; }
      }
    }
    if (!target) return false;
    target.click();
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function setQuantity(qty) {
    var input = document.querySelector('.quantity-selector--product .quantity-selector__value');
    if (!input) return false;
    input.value = qty;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function markMergePrevention() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'properties[_tpu_change_qty]';
    hidden.value = oldKey;
    form.appendChild(hidden);
  }

  // Attempts to carry over the same uploaded file by reconstructing its
  // ucarecdn.com URL from the UUID embedded in the stored property value —
  // same uu= extraction tpu-file-preview.js already relies on. Verifies the
  // image actually loads before trusting it; falls back to a clear
  // re-upload prompt otherwise, so a bad carry-over never reaches checkout
  // silently.
  function carryOverFile(uploadedImageValue) {
    if (!uploadedImageValue) return;
    var uuidMatch = uploadedImageValue.match(/[?&]uu=([0-9a-f-]{36})/i);
    if (!uuidMatch) {
      showBanner('Please re-upload your design file below — we’ve kept your size, finish, and quantity.', '');
      return;
    }
    var fileUrl = 'https://ucarecdn.com/' + uuidMatch[1] + '/';

    var attrInput = document.querySelector('.uploadkit-attribute-container input[name="properties[Uploaded Image]"]');
    var thumbsContainer = document.querySelector('.uploadkit-thumbnails');
    if (!attrInput || !thumbsContainer) {
      showBanner('Please re-upload your design file below — we’ve kept your size, finish, and quantity.', '');
      return;
    }

    var img = document.createElement('img');
    img.className = 'uploadkit-thumbnail';
    img.dataset.tpuCarriedOver = 'true';
    img.onload = function () {
      attrInput.value = fileUrl;
      attrInput.dispatchEvent(new Event('input', { bubbles: true }));
      document.querySelectorAll('.uploadkit-field').forEach(function (field) {
        field.classList.add('tpu-note-ready');
      });
      showBanner('We’ve carried over your previous design file, size, finish, and quantity — review the price below and click Add to Cart to confirm.', 'success');
    };
    img.onerror = function () {
      console.error(LOG, 'carried-over file failed to load — falling back to re-upload prompt');
      img.remove();
      attrInput.value = '';
      showBanner('We couldn’t automatically re-attach your previous design file. Please re-upload it below — your size, finish, and quantity are still kept.', 'error');
    };
    img.src = fileUrl;
    thumbsContainer.appendChild(img);
  }

  fetch('/cart.js', { credentials: 'same-origin' })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var item = cart.items.filter(function (i) { return i.key === oldKey; })[0];
      if (!item) {
        console.log(LOG, 'requote key not found in cart (stale link or cart already changed) — leaving form untouched');
        history.replaceState(null, '', location.pathname);
        return;
      }

      var widthRaw = findProperty(item.properties, 'width');
      var heightRaw = findProperty(item.properties, 'height');
      var filmTypeRaw = findProperty(item.properties, 'film type');
      var uploadedImageRaw = findProperty(item.properties, 'uploaded image');
      var qty = item.quantity;

      waitFor(function () {
        var root = document.getElementById('itoris_dynamicproductoptions');
        var wi = document.querySelector('.width input');
        var hi = document.querySelector('.height input');
        return (root && wi && hi) ? { root: root, wi: wi, hi: hi } : null;
      }, function (fields) {
        var w = parseFloat(widthRaw);
        var h = parseFloat(heightRaw);
        if (!isNaN(w) && !isNaN(h) && window.tpuPopulateDimensionsInches) {
          window.tpuPopulateDimensionsInches(w, h);
        }

        if (!selectFilmType(fields.root, filmTypeRaw)) {
          console.log(LOG, 'could not auto-select film type "' + filmTypeRaw + '" — left at default, customer can adjust manually');
        }

        setQuantity(qty);
        markMergePrevention();
        carryOverFile(uploadedImageRaw);

        if (window.dpoOptions) window.dpoOptions.updatePrice();

        if (!document.querySelector('.tpu-requote-banner')) {
          showBanner('We’ve pre-filled your last design’s size, finish, and quantity — review the price below and click Add to Cart to confirm.', '');
        }
      });
    })
    .catch(function (err) {
      console.error(LOG, err);
    });

  var handledAdd = false;
  document.addEventListener('product:added', function () {
    if (handledAdd) return;
    handledAdd = true;

    var updates = {};
    updates[oldKey] = 0;
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('failed to remove previous line (status ' + r.status + ')');
        return r.json();
      })
      .then(function () {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        history.replaceState(null, '', location.pathname);
        showBanner('Quantity updated — your cart now reflects the new amount. View your cart to confirm.', 'success');
      })
      .catch(function (err) {
        console.error(LOG, err);
        // Fail open: never silently double the order. The customer's new
        // line is already in the cart; leaving the old one visible (rather
        // than hiding this failure) is the safer outcome.
        showBanner('Your new quantity was added, but we couldn’t automatically remove the previous line — please remove it manually from your cart.', 'error');
      });
  });
})();
